<template>
  <div class="application-component completeness-analysis-component">
    <div class="application-component-title">充分公开分析</div>
    <div
      class="application-component-content"
      :style="{ fontSize }"
      v-loading="loading"
      :element-loading-text="loadingText"
    >
      <div v-if="readOnly" class="placeholder">
        <el-skeleton :rows="6" animated />
      </div>
      <template v-else>
        <div v-if="!hasAnnotationContent" class="dependency-tip">
          <div class="tip-title">充分公开分析依赖交底书正文内容</div>
          <div class="tip-desc">请先完成技术交底书解析后再生成充分公开分析结果。</div>
        </div>

        <template v-else>
          <div v-if="sourceStale" class="stale-tip">
            <span>交底书正文或特征拆解结果已更新，当前充分公开分析结果可能已过期。</span>
          </div>

          <div class="panel-head">
            <div>
              <div class="panel-meta">共识别 {{ issues.length }} 个潜在问题，点击行空白处可定位交底书原文。</div>
            </div>
            <div class="panel-actions">
              <el-button size="small" :loading="initializing" @click="handleInitialize">重新生成</el-button>
              <el-button size="small" @click="handleAddIssue">新增问题</el-button>
            </div>
          </div>

          <div v-if="issues.length" class="analysis-table-scroll">
            <table class="analysis-table">
              <thead>
                <tr>
                  <th class="problem-column">问题描述</th>
                  <th class="type-column">问题类型</th>
                  <th class="suggestion-column">修改建议</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(issue, index) in issues"
                  :key="issue.id || index"
                  class="issue-row"
                  @click="handleFocusIssue(issue)"
                >
                  <td class="problem-column">
                    <div class="cell-card">
                      <el-input
                        v-model="issue.problemDescription"
                        type="textarea"
                        :autosize="{ minRows: 3, maxRows: 6 }"
                        resize="none"
                        @click.stop
                        @blur="handleIssueBlur"
                      />
                      <div class="cell-foot">
                        <span class="issue-index">问题 {{ index + 1 }}</span>
                      </div>
                    </div>
                  </td>
                  <td class="type-column">
                    <div class="cell-card compact">
                      <el-select
                        v-model="issue.issueType"
                        filterable
                        allow-create
                        default-first-option
                        :reserve-keyword="false"
                        class="type-select"
                        placeholder="请选择或输入问题类型"
                        @click.stop
                        @change="handleIssueBlur"
                      >
                        <el-option
                          v-for="item in issueTypeOptions"
                          :key="item"
                          :label="item"
                          :value="item"
                        />
                      </el-select>
                    </div>
                  </td>
                  <td class="suggestion-column">
                    <div class="cell-card">
                      <el-input
                        v-model="issue.suggestion"
                        type="textarea"
                        :autosize="{ minRows: 3, maxRows: 6 }"
                        resize="none"
                        @click.stop
                        @blur="handleIssueBlur"
                      />
                      <div class="cell-actions">
                        <button class="action-button" type="button" @click.stop="handleAddIssueToDetail(issue)">
                          加入交流清单
                        </button>
                        <button class="action-button danger" type="button" @click.stop="handleDeleteIssue(index)">
                          删除
                        </button>
                      </div>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <el-empty
            v-else
            description="暂未识别出明确的完整性问题，可手动新增问题。"
          />
        </template>
      </template>
    </div>
  </div>
</template>

<script setup>
import { ElMessage } from 'element-plus'
import { computed, ref, watch } from 'vue'
import { useGlobalStore } from '@/store/modules/global'
import { useWritingStore } from '@/store/modules/writing'
import {
  fetchInitializeCompletenessAnalysis,
  fetchSaveComponent
} from '@/api/writing'
import { jsonParse } from '@/utils'
import {
  buildCompletenessAddIssueResult,
  buildCompletenessSourceVersion,
  isCompletenessSourceStale
} from './completenessAnalysisState.mjs'

const COMPONENT_TYPE_CODE = 'comp_cmp'
const ANNOTATION_COMPONENT_CODE = 'comp_ann'
const FEATURE_BREAKDOWN_COMPONENT_CODE = 'comp_fea'
const DETAIL_LIST_COMPONENT_CODE = 'comp_comm'
const FEATURE_BREAKDOWN_EVENT = 'feature-breakdown-focus'

const issueTypeOptions = [
  '技术问题不清',
  '技术步骤缺失',
  '实现细节不足',
  '技术实现不清',
  '技术效果不充分',
  '输入输出不明确',
  '关键参数缺失'
]

const globalStore = useGlobalStore()
const writingStore = useWritingStore()

const props = defineProps({
  readOnly: {
    type: Boolean,
    default: false
  },
  data: {
    type: Object,
    default: () => null
  },
  projectId: {
    type: String,
    default: ''
  },
  handleFocusMainFileSource: {
    type: Function,
    default: null
  },
  handleAddToDetailList: {
    type: Function,
    default: null
  },
  handleUpdateDetailList: {
    type: Function,
    default: null
  }
})

const resultData = ref(null)
const initializing = ref(false)
const hasAutoInitialized = ref(false)

const fontSize = computed(() => `${globalStore.fontSize}px`)
const loading = computed(() => initializing.value)
const loadingText = computed(() => initializing.value ? '正在思考中...' : '')

const annotationRaw = computed(() => writingStore.step2Info?.step2Component?.[ANNOTATION_COMPONENT_CODE]?.docResult || '')
const featureRaw = computed(() => writingStore.step2Info?.step2Component?.[FEATURE_BREAKDOWN_COMPONENT_CODE]?.docResult || '')

const annotationItems = computed(() => {
  const parsed = jsonParse(annotationRaw.value, [])
  if (!Array.isArray(parsed)) {
    return []
  }
  return parsed.filter(item => item?.type === 'TEXT' && String(item?.text || '').trim())
})

const featureItems = computed(() => {
  const parsed = jsonParse(featureRaw.value, [])
  return Array.isArray(parsed) ? parsed : []
})

const hasAnnotationContent = computed(() => annotationItems.value.length > 0)
const currentSourceVersion = computed(() => buildCompletenessSourceVersion(annotationRaw.value, featureRaw.value))
const issues = computed(() => resultData.value?.issues || [])
const sourceStale = computed(() => {
  return isCompletenessSourceStale({
    hasAnnotationContent: hasAnnotationContent.value,
    resultSourceVersion: resultData.value?.sourceVersion || '',
    currentSourceVersion: currentSourceVersion.value
  })
})

const normalizeIssue = (item = {}, index = 0) => {
  const sourceRanges = Array.isArray(item?.sourceRanges) ? item.sourceRanges : []
  const paragraphIndices = Array.isArray(item?.paragraphIndices)
    ? item.paragraphIndices.filter(value => Number.isInteger(value))
    : sourceRanges.map(range => range?.paragraphIndex).filter(value => Number.isInteger(value))

  return {
    id: item?.id || `completeness-issue-${Date.now()}-${index}`,
    problemDescription: item?.problemDescription || '',
    issueType: item?.issueType || issueTypeOptions[0],
    suggestion: item?.suggestion || '',
    paragraphIndices,
    sourceRanges
  }
}

const normalizeResult = (value) => {
  const parsed = typeof value === 'string' ? jsonParse(value, null) : value
  if (!parsed || typeof parsed !== 'object') {
    return null
  }

  const issueList = Array.isArray(parsed.issues) ? parsed.issues.map(normalizeIssue) : []
  return {
    sourceVersion: parsed.sourceVersion || '',
    updatedAt: parsed.updatedAt || '',
    paragraphCount: Number(parsed.paragraphCount || annotationItems.value.length || 0),
    featureCount: Number(parsed.featureCount || featureItems.value.length || 0),
    issueCount: Number(parsed.issueCount ?? issueList.length),
    issues: issueList
  }
}

const createBaseResult = () => {
  return {
    sourceVersion: currentSourceVersion.value,
    updatedAt: '',
    paragraphCount: annotationItems.value.length,
    featureCount: featureItems.value.length,
    issueCount: 0,
    issues: []
  }
}

const syncStepState = (nextResult) => {
  const normalized = normalizeResult(nextResult)
  if (!normalized) {
    return
  }

  resultData.value = normalized
  writingStore.setStep2Info({
    ...(writingStore.step2Info || {}),
    step2Component: {
      ...(writingStore.step2Info?.step2Component || {}),
      [COMPONENT_TYPE_CODE]: {
        ...(writingStore.step2Info?.step2Component?.[COMPONENT_TYPE_CODE] || {}),
        ...(props.data || {}),
        docResult: JSON.stringify(normalized)
      }
    }
  })
}

const persistResult = async (nextResult) => {
  const normalized = normalizeResult(nextResult)
  if (!normalized || !props.projectId) {
    return
  }

  normalized.issueCount = normalized.issues.length
  normalized.sourceVersion = normalized.sourceVersion || currentSourceVersion.value
  normalized.paragraphCount = normalized.paragraphCount || annotationItems.value.length
  normalized.featureCount = normalized.featureCount || featureItems.value.length

  await fetchSaveComponent({
    disclosureProjectId: props.projectId,
    componentTypeCode: COMPONENT_TYPE_CODE,
    docResult: JSON.stringify(normalized)
  })
  syncStepState(normalized)
}

const applyBackendResult = (result, successText = '') => {
  const normalized = normalizeResult(result)
  if (!normalized) {
    throw new Error('invalid_completeness_result')
  }

  syncStepState(normalized)
  if (successText) {
    ElMessage.success(successText)
  }
}

const handleInitialize = async () => {
  if (!props.projectId || initializing.value || !hasAnnotationContent.value) {
    return
  }

  initializing.value = true
  try {
    const result = await fetchInitializeCompletenessAnalysis(props.projectId)
    applyBackendResult(result, '充分公开已更新')
  } catch (error) {
    console.error('initialize completeness analysis failed', error)
    ElMessage.error('充分公开初始化失败')
  } finally {
    initializing.value = false
  }
}

const handleIssueBlur = async () => {
  if (!resultData.value) {
    return
  }

  try {
    await persistResult({
      ...resultData.value,
      issueCount: issues.value.length,
      issues: issues.value
    })
  } catch (error) {
    console.error('save completeness analysis failed', error)
    ElMessage.error('充分公开保存失败')
  }
}

const createEmptyIssue = () => {
  return normalizeIssue({
    id: `completeness-issue-${Date.now()}`,
    issueType: issueTypeOptions[0]
  }, issues.value.length)
}

const handleAddIssue = async () => {
  const nextResult = normalizeResult(buildCompletenessAddIssueResult({
    resultData: resultData.value || createBaseResult(),
    currentSourceVersion: currentSourceVersion.value,
    annotationCount: annotationItems.value.length,
    featureCount: featureItems.value.length,
    nextIssue: createEmptyIssue()
  }))

  try {
    await persistResult(nextResult)
  } catch (error) {
    console.error('add completeness issue failed', error)
    ElMessage.error('新增问题失败')
  }
}

const handleDeleteIssue = async (index) => {
  const nextIssues = issues.value.filter((_, issueIndex) => issueIndex !== index)
  const nextResult = normalizeResult({
    ...(resultData.value || createBaseResult()),
    issues: nextIssues
  })

  try {
    await persistResult(nextResult)
  } catch (error) {
    console.error('delete completeness issue failed', error)
    ElMessage.error('删除问题失败')
  }
}

const handleFocusIssue = (issue) => {
  if (!Array.isArray(issue?.sourceRanges) || !issue.sourceRanges.length) {
    ElMessage.warning('当前问题未关联原文段落，请重新计算或手动补充')
    return
  }

  if (props.handleFocusMainFileSource) {
    props.handleFocusMainFileSource({
      itemId: issue?.id || '',
      sourceRanges: issue.sourceRanges
    })
    return
  }

  window.dispatchEvent(new CustomEvent(FEATURE_BREAKDOWN_EVENT, {
    detail: {
      projectId: props.projectId,
      sourceRanges: issue.sourceRanges
    }
  }))
}

const buildDetailItem = (issue) => {
  return {
    type: 'text',
    sourceId: `completeness:${issue.id}`,
    question: `[完整性问题] ${issue.issueType || '待补充'}`,
    answer: [
      '【完整性问题】',
      '',
      issue.problemDescription || '待补充问题描述',
      `建议补充以下内容：${issue.suggestion || '请补充相关技术实现细节。'}`
    ].join('\n')
  }
}

const handleAddIssueToDetail = (issue) => {
  const nextItem = buildDetailItem(issue)

  if (props.handleAddToDetailList) {
    props.handleAddToDetailList(nextItem)
  } else if (props.handleUpdateDetailList) {
    const currentList = jsonParse(
      writingStore.step2Info?.step2Component?.[DETAIL_LIST_COMPONENT_CODE]?.docResult || '[]',
      []
    )
    const nextList = Array.isArray(currentList) ? [...currentList] : []
    const targetIndex = nextList.findIndex(item => item?.sourceId === nextItem.sourceId)
    if (targetIndex === -1) {
      nextList.push(nextItem)
    } else {
      nextList[targetIndex] = nextItem
    }
    props.handleUpdateDetailList(nextList)
  } else {
    return
  }

  ElMessage.success('已加入交流清单')
}

watch(
  () => props.data?.docResult,
  (newValue) => {
    resultData.value = normalizeResult(newValue)
  },
  { immediate: true }
)

watch(
  () => `${annotationRaw.value}||${featureRaw.value}`,
  async (newValue) => {
    if (!newValue || props.readOnly || resultData.value || hasAutoInitialized.value || !hasAnnotationContent.value) {
      return
    }

    hasAutoInitialized.value = true
    await handleInitialize()
  },
  { immediate: true }
)
</script>

<style scoped lang="scss">
@import './common.scss';

.dependency-tip,
.stale-tip,
.panel-head {
  padding: 14px 16px;
  border-radius: 14px;
}

.dependency-tip {
  background: #fff7ed;
  border: 1px solid #fed7aa;
}

.tip-title,
.panel-title {
  color: #111827;
  font-weight: 600;
}

.tip-desc,
.panel-meta {
  margin-top: 6px;
  line-height: 1.7;
  color: #4b5563;
}

.stale-tip {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  color: #9a3412;
  background: #fff7ed;
  border: 1px solid #fdba74;
}

.panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-top: 14px;
  background: #fff;
  border: 1px solid #e5e7eb;
}

.panel-meta {
  margin-top: 4px;
  font-size: inherit;
}

.panel-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.analysis-table-scroll {
  margin-top: 16px;
  overflow: auto;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
}

.analysis-table {
  width: 100%;
  min-width: 980px;
  border-collapse: separate;
  border-spacing: 0;
  table-layout: fixed;

  th,
  td {
    padding: 14px 12px;
    font-size: inherit;
    vertical-align: top;
    border-bottom: 1px solid #e5e7eb;
    border-right: 1px solid #e5e7eb;
  }

  th:last-child,
  td:last-child {
    border-right: none;
  }

  thead th {
    position: sticky;
    top: 0;
    z-index: 1;
    color: #111827;
    font-weight: 600;
    background: #f8fafc;
  }

  tbody tr:last-child td {
    border-bottom: none;
  }
}

.problem-column {
  width: 34%;
}

.type-column {
  width: 18%;
}

.suggestion-column {
  width: 48%;
}

.issue-row {
  cursor: pointer;

  &:hover td {
    background: #fcfdfd;
  }
}

.cell-card {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.cell-card.compact {
  min-height: 100%;
}

.cell-foot {
  display: flex;
  align-items: center;
  gap: 10px;
}

.issue-index {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: fit-content;
  padding: 2px 8px;
  color: #0f766e;
  font-size: inherit;
  font-weight: 600;
  background: #ecfdf5;
  border-radius: 999px;
}

.type-select {
  width: 100%;
}

.cell-actions {
  display: flex;
  align-items: center;
  gap: 16px;
}

.action-button {
  padding: 0;
  color: #0f766e;
  font-size: inherit;
  background: transparent;
  border: none;
  cursor: pointer;

  &.danger {
    color: #b91c1c;
  }
}

.placeholder {
  :deep(.el-skeleton__item) {
    border-radius: 8px;
  }
}

:deep(.el-textarea),
:deep(.el-textarea__inner),
.type-select :deep(.el-select__wrapper),
.type-select :deep(.el-select__selected-item),
.type-select :deep(.el-select__placeholder) {
  font-size: inherit;
}

@media (max-width: 960px) {
  .panel-head,
  .stale-tip {
    flex-direction: column;
    align-items: stretch;
  }

  .panel-actions {
    justify-content: flex-end;
  }
}
</style>
